import { ReactNode } from 'react';
import { Field } from 'react-final-form';
import { useUniqueIdentifier } from '@rainbow-modules/hooks';
import { Button, Modal, RenderIf } from 'react-rainbow-components';
import { UniversalFormModalProps } from '@rainbow-modules/forms/lib/components/UniversalFormModal';
import { ButtonProps } from 'react-rainbow-components/components/Button';
import { ModalProps } from 'react-rainbow-components/components/Modal';
import UniversalForm from 'components/UniversalForm';
import Trash from 'components/icons/trash';
import { validateNotEmpty } from 'data/validators';
import useConfirmModal from '../hooks/useConfirmModal';
import {
    ModalContainer,
    ModalDescription,
    ModalIconContainer,
    ModalTitle,
    ModalBody,
    NoteInput,
    ButtonsContainer,
} from './styled';

const buttonStyles = {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    marginLeft: '0.7rem',
};

const CancelModal = ({
    id,
    cancelationReasonRequired = false,
}: {
    id: string;
    cancelationReasonRequired?: boolean;
}) => {
    const { modalProps } = useConfirmModal(id);
    const {
        icon,
        variant,
        header,
        question,
        onSubmit,
        initialValues,
        cancelButtonLabel,
        okButtonLabel,
        borderRadius,
        onRequestClose,
        ...rest
    } = modalProps;

    const uniqueId = useUniqueIdentifier('cancel-form');

    const modalIcon = (icon ?? <Trash />) as ReactNode;

    const title = (header ?? 'Cancel Reminder?') as ReactNode;

    const description = (question ?? 'Once you confirm, this reminder will be marked as canceled.') as ReactNode;

    return (
        <Modal
            {...rest}
            onRequestClose={onRequestClose as ModalProps['onRequestClose']}
            title=""
            footer={(
                <ButtonsContainer>
                    <Button
                        // eslint-disable-next-line react/forbid-component-props
                        style={buttonStyles}
                        label={cancelButtonLabel as string}
                        variant="border"
                        onClick={onRequestClose as ModalProps['onRequestClose']}
                        borderRadius={borderRadius as ButtonProps['borderRadius']}
                    />
                    <Button
                        // eslint-disable-next-line react/forbid-component-props
                        style={buttonStyles}
                        label={okButtonLabel as string}
                        variant={variant as ButtonProps['variant']}
                        type="submit"
                        form={uniqueId}
                        borderRadius={borderRadius as ButtonProps['borderRadius']}
                    />
                </ButtonsContainer>
            )}
        >
            <UniversalForm
                id={uniqueId}
                initialValues={initialValues as UniversalFormModalProps['initialValues']}
                onSubmit={onSubmit as UniversalFormModalProps['onSubmit']}
            >
                <ModalContainer>
                    <ModalIconContainer>
                        {modalIcon}
                    </ModalIconContainer>
                    <ModalTitle>{title}</ModalTitle>
                    <ModalBody>
                        <ModalDescription>
                            {description}
                        </ModalDescription>
                        <RenderIf isTrue={cancelationReasonRequired}>
                            <Field
                                name="reason"
                                component={NoteInput}
                                variant="shaded"
                                labelAlignment="left"
                                placeholder="Add the cancelation reason ..."
                                rows={3}
                                required
                                validate={validateNotEmpty('Cancelation reason is required.')}
                            />
                        </RenderIf>
                    </ModalBody>
                </ModalContainer>
            </UniversalForm>
        </Modal>
    );
};

export default CancelModal;
